import { flatMap } from 'lodash-es';
import moment from 'moment-timezone';

import { CampaignActiveInactive, IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { IAdserverCreative, IAdserverGlobalMediafile } from '~/src/dash/api/console-api';
import { createChipWihFadingBackgroundVariations } from '~/src/dash/components/model-chip';
import { ValidBannerDimensions, ValidBannerMimetypes } from '~/src/dash/utils/banner-ads-helpers';
import { hasCampaignEnded, hasCampaignStarted } from '~/src/dash/utils/date';

export enum ECampaignStatus {
  Delivering,
  Ready,
  NotStartedYet,
  Completed,
  Inactive,
  Untargeted,
  Unknown,
  MediafileError,
  GoalMet,
  Draft,
}

export enum ERunningCampaignStatus {
  Running = 'RUNNING',
  NotRunning = 'NOT RUNNING',
}

export const RUNNING_CAMPAIGN_STATUSES: ECampaignStatus[] = [
  ECampaignStatus.Delivering,
  ECampaignStatus.Ready,
  ECampaignStatus.NotStartedYet,
  ECampaignStatus.Untargeted,
  ECampaignStatus.Unknown,
  ECampaignStatus.MediafileError,
];

export const NOT_RUNNING_CAMPAIGN_STATUSES: ECampaignStatus[] = [
  ECampaignStatus.Completed,
  ECampaignStatus.Inactive,
  ECampaignStatus.GoalMet,
  ECampaignStatus.Draft,
];

export interface ICampaignStatusInfo {
  component: any;
  tooltip: string;
}

export function computeCampaignStatus(
  c: IAdserverCampaign,
  publisherTimezone: string,
  impressions: number,
): ECampaignStatus {
  if (impressions >= parseInt(c.Quantity, 10)) {
    return ECampaignStatus.GoalMet;
  }
  if (hasCampaignEnded(moment.tz(c.EndDate, publisherTimezone))) {
    return ECampaignStatus.Completed;
  }

  let noGlobalMediaFile = false;

  const SubgroupsAdserverCreative = flatMap(
    c.AdserverCreativeFlightDatesSubgroups,
    'AdserverCreatives',
  );
  const AdserverCreatives: IAdserverCreative[] =
    c.EnableFlightDateSubgroups === 2 && SubgroupsAdserverCreative.length > 0
      ? SubgroupsAdserverCreative
      : c.AdserverCreatives;

  AdserverCreatives.forEach((creative) => {
    if (
      creative.AdserverGlobalMediafileIDs.length === 0 &&
      creative.AdserverGlobalVastID === undefined
    ) {
      noGlobalMediaFile = true;
      return;
    }
  });

  if (AdserverCreatives.length === 0 || noGlobalMediaFile) {
    return ECampaignStatus.Draft;
  }
  if (c.Sites === undefined) {
    return ECampaignStatus.Unknown;
  }
  if (c.Active === CampaignActiveInactive) {
    return ECampaignStatus.Inactive;
  }
  if (isCampaignMediafileError(c)) {
    return ECampaignStatus.MediafileError;
  }
  if (!c.IsTargetedByHB) {
    return ECampaignStatus.Untargeted;
  }
  if (!hasCampaignStarted(moment.tz(c.StartDate, publisherTimezone))) {
    return ECampaignStatus.NotStartedYet;
  }
  if (impressions === 0) {
    return ECampaignStatus.Ready;
  }
  return ECampaignStatus.Delivering;
}

export const ChipUntargeted = createChipWihFadingBackgroundVariations(
  '#F0072C',
  'rgba(255, 5, 5, 0.10)',
  'rgba(255, 5, 5, 0.05)',
  'rgba(255, 5, 5, 0.15)',
  true,
  true,
);

export const ChipDelivering = createChipWihFadingBackgroundVariations(
  '#00C488',
  'rgba(0,196,136,0.10)',
  'rgba(0,196,136,0.01)',
  'rgba(0,196,136,0.15)',
  true,
  true,
);

export const ChipCompleted = createChipWihFadingBackgroundVariations(
  '#5865E8',
  'rgba(88,101,232,0.10)',
  'rgba(88,101,232,0.05)',
  'rgba(88,101,232,0.15)',
  true,
  true,
);

export const ChipGoalMet = createChipWihFadingBackgroundVariations(
  '#5865E8',
  'rgba(88,101,232,0.10)',
  'rgba(88,101,232,0.05)',
  'rgba(88,101,232,0.15)',
  true,
  true,
);

export const ChipInactive = createChipWihFadingBackgroundVariations(
  '#707070',
  'rgba(112,112,112,0.10)',
  'rgba(112,112,112,0.05)',
  'rgba(112,112,112,0.15)',
  true,
  true,
);

export const ChipDraft = createChipWihFadingBackgroundVariations(
  '#707070',
  'rgba(112,112,112,0.10)',
  'rgba(112,112,112,0.05)',
  'rgba(112,112,112,0.15)',
  true,
  true,
);

export const ChipPending = createChipWihFadingBackgroundVariations(
  '#D08740',
  'rgba(254,247,234,1)',
  'rgba(254,247,234,0.5)',
  'rgba(254,247,234,0.15)',
  true,
  true,
);

export function campaignStatusToString(status: ECampaignStatus): string {
  if (status === undefined) {
    return 'loading...';
  }

  switch (status) {
    case ECampaignStatus.Unknown:
      return 'loading...';
    case ECampaignStatus.Inactive:
      return 'Inactive';
    case ECampaignStatus.Draft:
      return 'Draft';
    case ECampaignStatus.NotStartedYet:
      return 'Upcoming';
    case ECampaignStatus.Delivering:
      return 'Delivering';
    case ECampaignStatus.Ready:
      return 'Ready';
    case ECampaignStatus.Completed:
      return 'Completed';
    case ECampaignStatus.Untargeted:
      return 'Untargeted';
    case ECampaignStatus.MediafileError:
      return 'Invalid Media File';
    case ECampaignStatus.GoalMet:
      return 'Delivery Goal Met';
    default:
      return 'Unknown Status';
  }
}

export function campaignStatusToChip(status: ECampaignStatus): ICampaignStatusInfo | undefined {
  if (status === undefined) {
    return undefined;
  }

  switch (status) {
    case ECampaignStatus.Unknown:
      return undefined;
    case ECampaignStatus.Inactive:
      return {
        component: ChipInactive,
        tooltip: 'Campaign tag is inactive',
      };
    case ECampaignStatus.Draft:
      return {
        component: ChipDraft,
        tooltip: 'Campaign has no creatives',
      };
    case ECampaignStatus.NotStartedYet:
      return {
        component: ChipPending,
        tooltip: 'Campaign start date is in the future',
      };
    case ECampaignStatus.Delivering:
      return {
        component: ChipDelivering,
        tooltip: 'Campaign is delivering',
      };
    case ECampaignStatus.Ready:
      return {
        component: ChipPending,
        tooltip:
          'Campaign has started but the first impressions are still pending. As soon as a first impression is recorded campaign status will update to "Delivering".',
      };
    case ECampaignStatus.Completed:
      return {
        component: ChipCompleted,
        tooltip: 'Campaign ended',
      };
    case ECampaignStatus.Untargeted:
      return {
        component: ChipUntargeted,
        tooltip: 'Campaign isn’t targeted to any bidder. Go to bidders to complete setup.',
      };
    case ECampaignStatus.MediafileError:
      return {
        component: ChipUntargeted,
        tooltip:
          'Media File uploaded is corrupted or fails to meet banner requirements, please fix it to enable campaign delivery',
      };
    case ECampaignStatus.GoalMet:
      return {
        component: ChipGoalMet,
        tooltip: 'Delivery Goal met.  Campaign no longer serving.',
      };
    default:
      return undefined;
  }
}

export const isMediafileValid = (mediafile: IAdserverGlobalMediafile): boolean => {
  if (mediafile.CurrentlyUploading) {
    return true;
  }
  if (!ValidBannerMimetypes.includes(mediafile.Type)) {
    return !!(mediafile.Width && mediafile.Height && mediafile.Duration);
  }
  return isBannerImageValid(mediafile);
};

export const isBannerImageValid = (mediafile: IAdserverGlobalMediafile): boolean => {
  const dimensions = mediafile.Width.toString() + ',' + mediafile.Height.toString();
  return (
    ValidBannerMimetypes.includes(mediafile.Type) && ValidBannerDimensions.includes(dimensions)
  );
};

export function isCampaignMediafileError(campaign: IAdserverCampaign): boolean {
  for (const creative of campaign.AdserverCreatives) {
    if (creative.AdserverGlobalMediafiles) {
      for (const mediafile of creative.AdserverGlobalMediafiles) {
        if (!isMediafileValid(mediafile)) {
          return true;
        }
      }
    }
  }
  return false;
}
